/**
 * LINKURIOUS CONFIDENTIAL
 * Copyright Linkurious SAS 2012 - 2018
 *
 * Created by maximeallex on 2018-03-06.
 */


'use strict';

import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges} from '@angular/core';
import { IconNames, ICONS_REF } from './svg';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'icon',
  template: `<svg xmlns="http://www.w3.org/2000/svg"
                  [attr.viewBox]="viewBox" 
                  [innerHTML]="path"
                  [style.height]="iconSize"
                  [style.width]="iconSize"
                  [style.stroke]="strokeColor"
                  [style.strokeWidth]="stroke"
                  [class.stroke]="isStroke()"
                  [style.fill]="fillColor"></svg>`,
  styles: [
    "svg { stroke-width: 3px; stroke-linecap: round; display: inline-block; vertical-align: top; }",
    "svg.stroke { stroke: currentColor; fill: none }",
    "svg:not(.stroke) { fill: currentColor }",
    ".small { height : 20px; width: 20px; }",
    ".medium { height : 40px; width: 40px; }",
    ".large { height : 60px; width: 60px; }"
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IconComponent implements OnChanges {

  @Input('name') name: IconNames;
  @Input('size') size?: 'small'|'medium'|'large'|number = 'small';
  @Input('stroke') stroke?: string|undefined;
  @Input('color') color?: string|{[key: string]: string};
  public path: SafeHtml;
  public viewBox: string;
  public strokeColor: string;
  public fillColor: string;
  public iconSize: string;
  private _sizes = {
    small: 20,
    medium: 35,
    large: 50
  };

  constructor(
    private _sanitizer: DomSanitizer,
    private _changeDetector: ChangeDetectorRef
  ) {}

  ngOnChanges() {
    this.generateSize();
    this.generateColor();
    let icon = ICONS_REF[`${this.name}${( this.isStroke() ) ? '_st' : ''}`];
    if ( !icon ) {
      console.warn(`There's no icon with name '${this.name}'...`);
    }
    this.viewBox = (icon) ? icon.viewBox : ICONS_REF.logo_st.viewBox;
    this.path = this._sanitizer.bypassSecurityTrustHtml(
      (icon) ? icon.path : ICONS_REF.logo_st.path
    );
    this._changeDetector.markForCheck();
  }

  /**
   * Return true if the icon is a stroke
   */
  public isStroke(): boolean {
    return this.stroke !== null && this.stroke !== undefined;
  }

  /**
   * Generate the right size for the icon depending of the size given by the user or specific styles
   * applied
   */
  private generateSize(): void {
    if ( IconComponent.isNumber(this.size) ) {
      this.iconSize = `${this.size}px`;
    } else {
      this.iconSize = `${( this._sizes[this.size] ) ? this._sizes[this.size] : 20}px`;
    }
  }

  /**
   * Generate the right color depending if the color attribute exist or not and the icon type
   */
  private generateColor(): void {
    if ( this.isStroke() ) {
      this.strokeColor = this.getColor();
    } else {
      this.fillColor = this.getColor();
    }
  }

  /**
   * Return the right color form this.color
   */
  private getColor(): string {
    if ( typeof this.color === 'object' && !Array.isArray(this.color) ) {
      return Object.keys(this.color).reduce((r, c) => ( this.color[c] ) ? c : r, undefined);
    }
    return this.color as string;
  }

  /**
   * Return true if the value is a number
   */
  private static isNumber(n) {
    // string: try to parse as number (allow ',' as decimal separator)
    if (typeof n === 'string') {

      // prevent the empty string to be parsed as 0
      if (n.trim() === '') { n = Number.NaN; }

      n = +(n.replace(',', '.'));
    }

    // (the string could not be parsed) OR (it was neither string nor number)
    if (typeof n !== 'number') { n = Number.NaN; }

    // false for NaN, +Infinity, -Infinity
    if (!isFinite(n)) { n =  Number.NaN; }

    return n === n;
  }
}
